import { type ReactNode, useId } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import {
  type FieldValues,
  FormProvider,
  type SubmitHandler,
  type UseFormReturn,
} from 'react-hook-form';

export interface FormDialogProps<TFieldValues extends FieldValues> {
  open: boolean;
  title: string;
  form: UseFormReturn<TFieldValues>;
  onSubmit: SubmitHandler<TFieldValues>;
  onCancel: () => void;
  children: ReactNode;
  submitLabel?: string;
  cancelLabel?: string;
  /** Largura do diálogo. Default `sm`. */
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg';
  /** Erro a exibir acima dos campos (ex.: erro 500 do servidor). */
  serverError?: string | null;
}

/**
 * Wrapper de Dialog + react-hook-form. O `form.formState.isSubmitting`
 * controla o botão submit; erros de campo já são exibidos pelos próprios
 * inputs via `Controller`/`register` registrados em `form`.
 */
export function FormDialog<TFieldValues extends FieldValues>({
  open,
  title,
  form,
  onSubmit,
  onCancel,
  children,
  submitLabel,
  cancelLabel,
  maxWidth = 'sm',
  serverError,
}: FormDialogProps<TFieldValues>): JSX.Element {
  const { t } = useTranslation();
  const titleId = useId();
  const isSubmitting = form.formState.isSubmitting;

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onCancel}
      aria-labelledby={titleId}
      fullWidth
      maxWidth={maxWidth}
    >
      <FormProvider {...form}>
        <Box
          component="form"
          // stopPropagation: FormDialogs podem ser aninhados (ex.: o dialog de
          // Documentos Fiscais é renderizado DENTRO do form de Empresa). MUI usa
          // portal no DOM, mas o evento de submit borbulha pela ÁRVORE REACT até
          // o form ancestral — sem isso, salvar o form interno dispara também o
          // submit do form externo (empresa gravava junto e fechava a pilha).
          onSubmit={(e) => {
            e.stopPropagation();
            void form.handleSubmit(onSubmit)(e);
          }}
          noValidate
        >
          <DialogTitle id={titleId} sx={{ pr: 6 }}>
            {title}
            <IconButton
              aria-label={cancelLabel ?? t('components.formDialog.cancel')}
              onClick={onCancel}
              disabled={isSubmitting}
              sx={{ position: 'absolute', right: 8, top: 8 }}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} sx={{ pt: 1 }}>
              {serverError && (
                <Box
                  role="alert"
                  sx={{
                    bgcolor: 'error.lighter',
                    color: 'error.dark',
                    p: 1.5,
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'error.light',
                    fontSize: 14,
                  }}
                >
                  {serverError}
                </Box>
              )}
              {children}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={onCancel} disabled={isSubmitting}>
              {cancelLabel ?? t('components.formDialog.cancel')}
            </Button>
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              {isSubmitting
                ? t('components.formDialog.saving')
                : (submitLabel ?? t('components.formDialog.save'))}
            </Button>
          </DialogActions>
        </Box>
      </FormProvider>
    </Dialog>
  );
}
